"use client";

// Persist the user's manual season selection to localStorage so it survives
// re-mounts of SeasonProvider that can occur during page navigation (e.g. a
// Suspense boundary in the admin layout suspending while a new route loads).
// Without this, navigating from /dashboard/teams (Season 4 picked) into a team
// detail page and then to Edit Team caused the picker to flip back to the
// auto-selected current season.

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useOrganization } from '@/context/OrganizationContext';
import { createLogger } from '@/utils/logger/devLogger';

const seasonLogger = createLogger('SeasonContext');

const SELECTED_SEASON_STORAGE_KEY = 'cheervue:selectedSeasonId';

const readStoredSeasonId = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(SELECTED_SEASON_STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStoredSeasonId = (seasonId: string | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (seasonId) {
      window.localStorage.setItem(SELECTED_SEASON_STORAGE_KEY, seasonId);
    } else {
      window.localStorage.removeItem(SELECTED_SEASON_STORAGE_KEY);
    }
  } catch {
    // Storage unavailable (private mode, quota) — fall through silently;
    // in-memory state still works, just won't survive remounts.
  }
};

export interface Season {
  id: string;
  organization_id: string;
  name: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  season_status: 'upcoming' | 'current' | 'past' | 'tryout' | 'archived';
  teams_count?: number;
}

interface SeasonContextValue {
  selectedSeason: Season | null;
  availableSeasons: Season[];
  setSelectedSeason: (season: Season | null) => void;
  refreshSeasons: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const SeasonContext = createContext<SeasonContextValue | undefined>(undefined);

export const useSeason = () => {
  const context = useContext(SeasonContext);
  if (!context) {
    throw new Error('useSeason must be used within SeasonProvider');
  }
  return context;
};

interface SeasonProviderProps {
  children: ReactNode;
}

export const SeasonProvider: React.FC<SeasonProviderProps> = ({ children }) => {
  const { currentOrganization } = useOrganization();
  const [selectedSeason, setSelectedSeasonState] = useState<Season | null>(null);
  const [availableSeasons, setAvailableSeasons] = useState<Season[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Wrap setSelectedSeason so every manual selection also persists to
  // localStorage. The provider can be re-mounted by Next.js Suspense during
  // route transitions; without persistence the user's pick is lost.
  const setSelectedSeason = (season: Season | null) => {
    writeStoredSeasonId(season?.id ?? null);
    setSelectedSeasonState(season);
  };

  // The gym whose seasons are actually in state. `currentOrganization` starts
  // null on every page load and arrives a moment later, so without this the
  // provider announced "done loading, no season" in that gap — and a consumer
  // that reads a season on mount acted on it. See `seasonsPending` below.
  const [loadedForOrgId, setLoadedForOrgId] = useState<string | null>(null);

  // The gym the user is actually on, as far as the fetching below is concerned.
  // Switching gyms while a read is in flight leaves two requests racing, and the
  // slower one can land second with the WRONG gym's seasons; a late answer that
  // marked the wrong gym loaded left every consumer of `isLoading` on a spinner
  // until the page was reloaded.
  //
  // Written by the EFFECT that starts each gym's read, never during render and
  // never by `refreshSeasons`. Two reasons. A render can be thrown away by React
  // and a ref write would survive it, leaving this pointing at a gym that never
  // committed — the same rollback trap the report's own sync avoids. And
  // `refreshSeasons` can be held in a stale closure by a caller that captured it
  // before a gym switch (season-management does exactly this across an await);
  // if such a call could claim currentness, it would invalidate the real gym's
  // in-flight read and nothing would ever re-schedule it.
  const latestOrgId = useRef<string | null>(null);

  const fetchSeasons = async ({ showLoading = false } = {}) => {
    const requestOrgId = currentOrganization?.id ?? null;
    if (!requestOrgId) {
      setIsLoading(false);
      return;
    }
    // True only while this call is for the gym the user is still on.
    const stillCurrent = () => latestOrgId.current === requestOrgId;
    // Started from a stale closure, for a gym the user has already left —
    // `refreshSeasons` is the one that can be held across a gym switch. Touch
    // NOTHING, spinner included: this call's own response is discarded, so a
    // spinner raised here would never come down and every consumer of
    // `isLoading` would sit on it until the page was reloaded.
    //
    // Deliberately the ONLY staleness check. An earlier version guarded here
    // AND in `refreshSeasons`, which read as defence in depth but was not: the
    // outer guard made this one unreachable, so either could be deleted with a
    // green suite and the second deletion re-opened the bug.
    if (!stillCurrent()) return;
    if (showLoading) setIsLoading(true);

    try {
      seasonLogger.info('Fetching seasons for organization', { organizationId: requestOrgId });

      const response = await fetch(`/api/seasons?organization_id=${requestOrgId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch seasons: ${response.statusText}`);
      }

      const data = await response.json();
      const seasons: Season[] = data.seasons || [];

      // Answered for a gym the user has already left. Applying it would show
      // the old gym's seasons under the new gym's name.
      if (!stillCurrent()) {
        seasonLogger.info('Discarding seasons for a gym that is no longer selected', {
          organizationId: requestOrgId,
        });
        return;
      }

      seasonLogger.info('Fetched seasons', { count: seasons.length });
      setAvailableSeasons(seasons);

      // This gym has no seasons at all, so nothing here is selectable. Holding
      // on to the PREVIOUS gym's season would leave every season-filtered page
      // quietly filtering this gym by a season that belongs to another one —
      // and with an empty list the picker does not render, so there is nothing
      // on screen to correct it with. The remembered choice in storage is left
      // alone: it is still right for the gym it came from.
      if (seasons.length === 0) {
        setSelectedSeasonState(null);
      }

      // Pick the season to display, in priority order:
      //   1. Current in-memory selection if it still belongs to this org's list.
      //   2. Last selection saved to localStorage (survives provider remounts).
      //   3. The org's "current" season, or the first season as a final fallback.
      if (seasons.length > 0) {
        setSelectedSeasonState(prev => {
          // Keep the user's PICK, not their stale copy of it. Matching on id
          // preserves which season is selected; returning the freshly fetched
          // row is what makes a rename (or a date/status edit) visible without
          // a page reload. Returning `prev` here held the pre-edit field values
          // for every consumer of `selectedSeason` until the tab was reloaded,
          // which sent a season name that no longer existed to
          // /api/teams/create/billing-summary and 400'd it (#3615).
          const refreshedPrev = prev ? seasons.find(s => s.id === prev.id) : undefined;
          if (refreshedPrev) {
            seasonLogger.info('Preserving user-selected season', { seasonName: refreshedPrev.name });
            return refreshedPrev;
          }

          const storedId = readStoredSeasonId();
          const storedSeason = storedId ? seasons.find(s => s.id === storedId) : undefined;
          if (storedSeason) {
            seasonLogger.info('Restoring user-selected season from storage', {
              seasonName: storedSeason.name,
            });
            return storedSeason;
          }

          const currentSeason = seasons.find(s => s.season_status === 'current');
          const seasonToSelect = currentSeason || seasons[0];
          seasonLogger.info('Auto-selecting season', {
            seasonName: seasonToSelect.name,
            status: seasonToSelect.season_status
          });
          writeStoredSeasonId(seasonToSelect.id);
          return seasonToSelect;
        });
      }

      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load seasons';
      seasonLogger.error('Error fetching seasons', { error: errorMessage });
      if (stillCurrent()) setError(errorMessage);
    } finally {
      // Settle ONLY on the read the user is actually waiting for. Marking the
      // gym loaded from a superseded read is what wedged every consumer on a
      // permanent spinner: the newer gym would never be marked loaded and
      // nothing schedules another read for it.
      //
      // Also runs on failure: we are no longer waiting, we simply have no
      // seasons. Leaving it unset holds every consumer on a spinner too.
      if (stillCurrent()) {
        setLoadedForOrgId(requestOrgId);
        setIsLoading(false);
      }
    }
  };

  // Use currentOrganization.id as dependency to prevent infinite loops
  // (object reference changes even when data stays the same)
  useEffect(() => {
    // This effect is the single authority on which gym is current: it runs on
    // every real change of gym, in order, and nothing else writes the ref.
    latestOrgId.current = currentOrganization?.id ?? null;
    fetchSeasons();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.id]);

  const refreshSeasons = async () => {
    // The spinner is raised inside `fetchSeasons`, after it has checked that
    // this call is still for the gym the user is on. See the note there.
    await fetchSeasons({ showLoading: true });
  };

  // "We do not know this gym's seasons yet" — computed during render, not from
  // an effect, so a consumer rendering in the SAME pass as the gym arriving
  // already sees it. The fetch is started by an effect on the PROVIDER, which
  // React runs AFTER its children's effects, so a plain `isLoading` flag
  // is still false when a child first acts on the new gym. Without this the
  // Team Attendance report spent an all-teams, all-seasons query on every cold
  // page open and threw the answer away a moment later.
  //
  // No gym at all is not "loading": that is a settled state (signed out, or a
  // user with no gym), and treating it as loading strands every consumer.
  const seasonsPending = Boolean(
    currentOrganization?.id && loadedForOrgId !== currentOrganization.id
  );

  const value: SeasonContextValue = {
    selectedSeason,
    availableSeasons,
    setSelectedSeason,
    refreshSeasons,
    isLoading: isLoading || seasonsPending,
    error
  };

  return (
    <SeasonContext.Provider value={value}>
      {children}
    </SeasonContext.Provider>
  );
};

